import { useState, useEffect } from 'react';
import { Save, Plus, FileText, Globe, TreePine, Check, GitCommit, History, Wand2, Upload } from 'lucide-react';
import { createContent, addSceneToContent } from '../services/content';
import RevisionHistoryModal from '../components/RevisionHistoryModal';

import { API_URL } from '../config/api';

export default function ContentWizard() {
  const [step, setStep] = useState(1);
  const [contentUuid, setContentUuid] = useState<string | null>(null);
  const [status, setStatus] = useState('draft');
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // بيانات الخطوة الأولى
  const [title, setTitle] = useState('');
  const [type, setType] = useState('story');
  const [masterAudioUrl, setMasterAudioUrl] = useState('');
  
  // بيانات الخطوة الثانية (المشاهد)
  const [scenes, setScenes] = useState<any[]>([]);
  const [srtText, setSrtText] = useState('');

  // بيانات الخطوة الثالثة (الترجمة والتراث)
  const [targetLang, setTargetLang] = useState('en');
  const [translatedTitle, setTranslatedTitle] = useState('');
  const [translatedScenes, setTranslatedScenes] = useState<{[key: number]: string}>({});
  
  const [places, setPlaces] = useState<any[]>([]);
  const [selectedPlace, setSelectedPlace] = useState('');

  useEffect(() => {
    if (step === 3) {
      const fetchPlaces = async () => {
        try {
          const token = localStorage.getItem('jwt_token') || '';
          const res = await fetch(`${API_URL}/heritage/places`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setPlaces(data);
          } else {
            setPlaces([{ uuid: 'p1', name: 'القلعة' }, { uuid: 'p2', name: 'المدينة القديمة' }]);
          }
        } catch (e) {
          setPlaces([{ uuid: 'p1', name: 'القلعة' }, { uuid: 'p2', name: 'المدينة القديمة' }]);
        }
      };
      fetchPlaces();
    }
  }, [step]);

  const handleStatusChange = async (newStatus: string) => {
    setStatus(newStatus);
    if (!contentUuid) return;
    try {
      const token = localStorage.getItem('jwt_token') || '';
      await fetch(`${API_URL}/workflow/${contentUuid}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
    } catch (e) {
      console.error('Failed to update status', e);
    }
  };

  const handleSaveVersion = async () => {
    if (!contentUuid) return;
    try {
      const token = localStorage.getItem('jwt_token') || '';
      const res = await fetch(`${API_URL}/workflow/${contentUuid}/version`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        alert('New version saved successfully');
      } else {
        alert('Failed to save version');
      }
    } catch (e) {
      alert('Error saving version');
    }
  };

  const handleCreateContent = async () => {
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('jwt_token') || '';
      const data = await createContent({ title, type, primary_language: 'ar' }, token);
      setContentUuid(data.uuid || 'mock-content-uuid');
      setStep(2);
    } catch (error: any) {
      alert(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAutoGenerateScenes = async () => {
    if (!contentUuid || !srtText.trim()) return;
    
    setIsProcessing(true);
    try {
      const blocks = srtText.trim().split(/\n\s*\n/);
      const parsedScenes = [];

      const timeToMs = (timeStr: string) => {
        const [time, ms] = timeStr.replace('.', ',').split(',');
        const [hours, minutes, seconds] = time.split(':');
        return (parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseInt(seconds)) * 1000 + parseInt(ms || '0');
      };

      for (let i = 0; i < blocks.length; i++) {
        const lines = blocks[i].split('\n').map(l => l.trim()).filter(l => l);
        if (lines.length >= 3) {
          const timeLine = lines[1];
          const textLines = lines.slice(2).join('\n');
          const [startStr, endStr] = timeLine.split(' --> ');
          
          if (startStr && endStr) {
            parsedScenes.push({
              sort_order: scenes.length + i + 1,
              start_time_ms: timeToMs(startStr.trim()),
              end_time_ms: timeToMs(endStr.trim()),
              body_text: textLines.trim(),
              language_code: 'ar'
            });
          }
        }
      }

      if (parsedScenes.length === 0) {
        throw new Error('لم يتم العثور على مشاهد صالحة. يرجى التأكد من صيغة SRT.');
      }

      const token = localStorage.getItem('jwt_token') || '';
      const createdScenes = [];
      
      for (const scene of parsedScenes) {
        const response = await addSceneToContent(contentUuid, scene, token);
        createdScenes.push({ ...scene, uuid: response?.sceneUuid || `mock-${Date.now()}-${Math.random()}` });
      }

      setScenes([...scenes, ...createdScenes]);
      setSrtText('');
      alert(`تم توليد ${createdScenes.length} مشهد بنجاح!`);
    } catch (error: any) {
      alert(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  
  const handleUpdateSceneText = async (sceneUuid: string, newText: string) => {
    try {
      const token = localStorage.getItem('jwt_token') || '';
      await fetch(`${API_URL}/contents/scenes/${sceneUuid}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ body_text: newText })
      });
      // Update local state
      setScenes(scenes.map(s => s.uuid === sceneUuid ? { ...s, body_text: newText } : s));
    } catch (e) {
      console.error('Failed to update scene', e);
    }
  };

  const handleTranslateScene = async (idx: number, text: string) => {
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('jwt_token') || '';
      const res = await fetch(`${API_URL}/ai/translate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, source_lang: 'arabic', target_lang: targetLang === 'en' ? 'english' : 'french' })
      });
      const data = await res.json();
      if(data.translated_text) {
        setTranslatedScenes(prev => ({ ...prev, [idx]: data.translated_text }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTranslateAll = async () => {
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('jwt_token') || '';
      const newTranslations = { ...translatedScenes };
      for (let i = 0; i < scenes.length; i++) {
        if (!newTranslations[i]) {
          const res = await fetch(`${API_URL}/ai/translate`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: scenes[i].body_text, source_lang: 'arabic', target_lang: targetLang === 'en' ? 'english' : 'french' })
          });
          const data = await res.json();
          if(data.translated_text) {
            newTranslations[i] = data.translated_text;
          }
        }
      }
      setTranslatedScenes(newTranslations);
    } catch (error) {
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImportTranslatedSRT = () => {
    const srt = prompt("قم بلصق محتوى SRT المترجم هنا:");
    if (!srt) return;
    
    const blocks = srt.trim().split(/\n\s*\n/);
    const newTranslations = { ...translatedScenes };
    
    for (let i = 0; i < Math.min(blocks.length, scenes.length); i++) {
      const lines = blocks[i].split('\n').map((l: string) => l.trim()).filter((l: string) => l);
      if (lines.length >= 3) {
        const textLines = lines.slice(2).join('\n');
        newTranslations[i] = textLines.trim();
      }
    }
    setTranslatedScenes(newTranslations);
  };


  const handleSaveTranslations = async () => {
    if (!contentUuid) return;
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('jwt_token') || '';
      
      const contentRes = await fetch(`${API_URL}/translations/${contentUuid}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          language_code: targetLang,
          title: translatedTitle
        })
      });
      if (!contentRes.ok) throw new Error('Failed to save content translation');

      for (let i = 0; i < scenes.length; i++) {
        const sceneText = translatedScenes[i];
        if (!sceneText) continue;
        const sceneUuid = scenes[i].uuid;
        const sceneRes = await fetch(`${API_URL}/translations/scenes/${sceneUuid}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            language_code: targetLang,
            text_content: sceneText
          })
        });
        if (!sceneRes.ok) throw new Error(`Failed to save translation for scene ${i + 1}`);
      }
      alert('تم حفظ الترجمات بنجاح!');
    } catch (error: any) {
      alert(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLinkHeritage = async () => {
    if (!contentUuid || !selectedPlace) return;
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('jwt_token') || '';
      const res = await fetch(`${API_URL}/heritage/link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content_uuid: contentUuid,
          place_uuid: selectedPlace
        })
      });
      if (!res.ok) throw new Error('Failed to link heritage node');
      alert('تم ربط المعلم التراثي بنجاح!');
    } catch (error: any) {
      alert(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-8 border-b border-slate-200 pb-4">
        <h1 className="text-2xl font-bold text-slate-800">محرر المحتوى</h1>
        <div className="flex gap-2">
          <span className={`px-3 py-1 rounded-full text-sm ${step === 1 ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'}`}>1. المعلومات الأساسية</span>
          <span className={`px-3 py-1 rounded-full text-sm ${step === 2 ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'}`}>2. الخط الزمني التلقائي</span>
          <span className={`px-3 py-1 rounded-full text-sm ${step === 3 ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'}`}>3. الترجمة والتراث</span>
        </div>
      </div>

      {contentUuid && (
        <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg p-4 mb-8">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-slate-700">Status:</span>
            <select
              value={status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="text-sm border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-slate-900"
            >
              <option value="draft">Draft</option>
              <option value="review">Review</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsHistoryOpen(true)}
              className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 bg-white border border-slate-300 px-3 py-1.5 rounded transition-colors"
            >
              <History className="w-4 h-4" /> View History
            </button>
            <button
              onClick={handleSaveVersion}
              className="flex items-center gap-2 text-sm text-white bg-slate-800 hover:bg-slate-900 px-3 py-1.5 rounded transition-colors"
            >
              <GitCommit className="w-4 h-4" /> Save as New Version
            </button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-6 bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">عنوان المحتوى</label>
            <input 
              type="text" 
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
              placeholder="مثال: حكاية الأسد الحكيم"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">رابط الملف الصوتي الموحد (Master Audio)</label>
            <input 
              type="url" 
              value={masterAudioUrl}
              onChange={(e) => setMasterAudioUrl(e.target.value)}
              className="w-full p-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
              placeholder="https://example.com/audio.mp3"
              dir="ltr"
            />
            <p className="text-xs text-slate-500 mt-2">استخدم ملف صوتي واحد فقط للقصة بأكملها.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">نوع المحتوى</label>
            <select 
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full p-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              <option value="story">قصة شعبية</option>
              <option value="podcast">بودكاست</option>
              <option value="poem">قصيدة / زجل</option>
            </select>
          </div>
          <button 
            onClick={handleCreateContent}
            disabled={!title || isProcessing}
            className="w-full bg-slate-900 text-white py-3 rounded-md hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            {isProcessing ? 'جاري الحفظ...' : 'حفظ والمتابعة لمحرر المشاهد'}
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="grid grid-cols-3 gap-6">
          {/* محرر الاستخراج التلقائي */}
          <div className="col-span-2 space-y-4 bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-500" /> الاستخراج التلقائي من SRT
            </h3>
            <p className="text-sm text-slate-600">
              قم بلصق محتوى ملف الترجمة (SRT) هنا، وسيقوم النظام باستخراج كافة المشاهد، التوقيتات بالملي ثانية، والنصوص بشكل تلقائي وفوري.
            </p>
            <textarea 
              value={srtText}
              onChange={(e) => setSrtText(e.target.value)}
              className="w-full p-3 border border-slate-300 rounded-md h-64 focus:outline-none focus:ring-2 focus:ring-slate-900 font-mono text-sm"
              placeholder="1\n00:00:01,000 --> 00:00:04,500\nكان يا ما كان في قديم الزمان...\n"
              dir="ltr"
            />

            <button 
              onClick={handleAutoGenerateScenes}
              disabled={isProcessing || !srtText.trim()}
              className="flex items-center justify-center gap-2 w-full bg-indigo-600 text-white py-3 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              <Plus className="w-5 h-5" /> {isProcessing ? 'جاري استخراج المشاهد...' : 'توليد المشاهد تلقائياً'}
            </button>
          </div>

          {/* قائمة المشاهد المدرجة */}
          <div className="col-span-1 bg-slate-50 p-4 rounded-lg border border-slate-200 overflow-y-auto max-h-[600px] flex flex-col">
            <h3 className="font-semibold text-slate-800 mb-4 border-b border-slate-200 pb-2">الخط الزمني المولد ({scenes.length})</h3>
            <div className="space-y-3 flex-1">
              {scenes.map((scene, idx) => (
                <div key={idx} className="bg-white p-3 rounded border border-slate-200 text-sm shadow-sm relative group">
                  <div className="flex justify-between text-xs text-indigo-500 mb-2 font-mono bg-indigo-50 px-2 py-1 rounded">
                    <span>{scene.start_time_ms}ms</span>
                    <span>{scene.end_time_ms}ms</span>
                  </div>
                  <textarea 
                    defaultValue={scene.body_text}
                    onBlur={(e) => handleUpdateSceneText(scene.uuid, e.target.value)}
                    className="w-full text-slate-700 bg-transparent border-none focus:ring-0 p-0 text-sm resize-none h-16"
                  />
                </div>
              ))}
              {scenes.length === 0 && <p className="text-sm text-slate-400 text-center py-8">قم بلصق الـ SRT لتوليد المشاهد.</p>}
            </div>
            
            <button 
              onClick={() => setStep(3)}
              className="w-full bg-slate-900 text-white py-3 rounded-md hover:bg-slate-800 transition-colors mt-6"
            >
              المتابعة للخطوة التالية
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="grid grid-cols-2 gap-6">
          {/* Translations Section */}
          <div className="space-y-6 bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Globe className="w-5 h-5 text-slate-500" /> الترجمة
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">لغة الترجمة</label>
              <select 
                value={targetLang}
                onChange={(e) => setTargetLang(e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
              >
                <option value="en">الإنجليزية (English)</option>
                <option value="fr">الفرنسية (Français)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">ترجمة العنوان</label>
              <input 
                type="text" 
                value={translatedTitle}
                onChange={(e) => setTranslatedTitle(e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
                placeholder="عنوان المحتوى باللغة المحددة"
                dir="ltr"
              />
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-slate-700">ترجمة المشاهد</label>
                <div className="flex gap-2">
                  <button onClick={handleImportTranslatedSRT} className="text-xs flex items-center gap-1 bg-slate-100 text-slate-600 px-2 py-1 rounded hover:bg-slate-200">
                    <Upload className="w-3 h-3" /> استيراد SRT مترجم
                  </button>
                  <button onClick={handleTranslateAll} disabled={isProcessing} className="text-xs flex items-center gap-1 bg-indigo-50 text-indigo-600 px-2 py-1 rounded hover:bg-indigo-100">
                    <Wand2 className="w-3 h-3" /> ترجمة كاملة (AI)
                  </button>
                </div>
              </div>
              {scenes.length > 0 ? scenes.map((scene, idx) => (
                <div key={idx} className="space-y-2 border-r-2 border-slate-200 pr-4">
                  <p className="text-xs text-slate-500 font-mono">{scene.start_time_ms}ms - {scene.end_time_ms}ms</p>
                  <div className="flex justify-between items-start gap-2 bg-slate-50 p-2 rounded">
                    <p className="text-sm text-slate-700">{scene.body_text}</p>
                    <button onClick={() => handleTranslateScene(idx, scene.body_text)} disabled={isProcessing} className="text-indigo-500 hover:text-indigo-700 p-1 bg-white rounded-full shadow-sm" title="ترجمة هذا المشهد">
                      <Wand2 className="w-4 h-4" />
                    </button>
                  </div>
                  <textarea 
                    value={translatedScenes[idx] || ''}
                    onChange={(e) => setTranslatedScenes({...translatedScenes, [idx]: e.target.value})}
                    className="w-full p-2 border border-slate-300 rounded-md h-20 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    placeholder="ترجمة هذا المشهد..."
                    dir="ltr"
                  />
                </div>
              )) : (
                <p className="text-sm text-slate-500 italic">لا توجد مشاهد مسجلة.</p>
              )}
            </div>

            <button 
              onClick={handleSaveTranslations}
              disabled={isProcessing}
              className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white py-3 rounded-md hover:bg-slate-800 transition-colors disabled:opacity-50"
            >
              <Save className="w-5 h-5" /> {isProcessing ? 'جاري الحفظ...' : 'حفظ الترجمات'}
            </button>
          </div>

          {/* Heritage Linking Section */}
          <div className="space-y-6 bg-white p-6 rounded-lg border border-slate-200 shadow-sm h-fit">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <TreePine className="w-5 h-5 text-slate-500" /> ربط شجرة التراث
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">المعلم التراثي المرتبط</label>
              <select 
                value={selectedPlace}
                onChange={(e) => setSelectedPlace(e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
              >
                <option value="">اختر معلماً...</option>
                {places.map((place) => (
                  <option key={place.uuid} value={place.uuid}>{place.name}</option>
                ))}
              </select>
            </div>

            <button 
              onClick={handleLinkHeritage}
              disabled={!selectedPlace || isProcessing}
              className="w-full flex items-center justify-center gap-2 bg-white border border-slate-300 text-slate-700 py-3 rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              <Check className="w-5 h-5" /> {isProcessing ? 'جاري الربط...' : 'تأكيد الربط'}
            </button>
          </div>
        </div>
      )}

      {isHistoryOpen && contentUuid && (
        <RevisionHistoryModal
          contentUuid={contentUuid}
          onClose={() => setIsHistoryOpen(false)}
          onRestoreSuccess={() => setIsHistoryOpen(false)}
        />
      )}
    </div>
  );
}